import { FC } from "react";

import { SATOSLink } from "@/app/application/SATOSLink";
import { Translate } from "@/app/application/Translate";
import {
	ParticipantMark,
	TrainingSessionParticipants,
} from "@/features/training/model/Training/TrainingSessionParticipants";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import PersonIcon from "@mui/icons-material/Person";
import {
	Avatar,
	Box,
	Chip,
	ChipProps,
	IconButton,
	List,
	ListItem,
	Paper,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";

import { useMutateDeleteTrainingSessionStudent } from "@/service/TrainingMutations";

interface CourseSessionStudentsProps {
	sessionId: number;
	students: TrainingSessionParticipants[];
	areEditable: boolean;
}

const markToColor = (mark?: string): ChipProps["color"] => {
	if (mark === ParticipantMark.Pass || mark === "Pass") return "success";
	if (mark === ParticipantMark.Fail || mark === "Fail") return "error";
	return "default";
};

export const CourseSessionStudents: FC<CourseSessionStudentsProps> = ({
	sessionId,
	students,
	areEditable,
}) => {
	const theme = useTheme();
	const deleteTrainingSessionStudent = useMutateDeleteTrainingSessionStudent();

	const handleDelete = async (sid: number, studentId: number) => {
		try {
			await deleteTrainingSessionStudent({ sessionId: sid, studentId });
			AlertRequest.success();
		} catch (error) {
			console.error(error);
			AlertRequest.failed();
		}
	};

	if (!students?.length) return null;

	const getInitials = (name?: string) => {
		if (!name) return "";
		const parts = name.trim().split(/\s+/);
		if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? "";
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	};

	return (
		<Paper
			variant="outlined"
			sx={{
				borderRadius: 2,
				borderColor: "divider",
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
				minHeight: 0,
				maxHeight: { xs: "45vh", sm: "52vh" },
			}}
		>
			<Box
				sx={{
					px: 2,
					py: 1.5,
					borderBottom: 1,
					borderColor: "divider",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					flexWrap: "wrap",
					gap: 1,
				}}
			>
				<Typography
					variant="overline"
					color="text.secondary"
					sx={{ letterSpacing: 0.8 }}
				>
					<Translate String="Training.Session.Students">Participants</Translate>
				</Typography>
				<Typography variant="caption" color="text.secondary" fontWeight={600}>
					{students.length}
				</Typography>
			</Box>

			<List
				disablePadding
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					overflowX: "hidden",
					pb: 0.5,
				}}
			>
				{students.map((student, idx) => {
					const initials = getInitials(student.Name);
					const hasInitials = Boolean(initials);
					const markColor = markToColor(student.Mark);
					const accentColor =
						markColor === "success"
							? theme.palette.success.main
							: markColor === "error"
								? theme.palette.error.main
								: theme.palette.divider;

					return (
						<ListItem
							key={student.Id}
							disablePadding
							disableGutters
							sx={{
								borderBottom: idx < students.length - 1 ? 1 : 0,
								borderColor: "divider",
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									width: "100%",
									gap: 2,
									px: 2,
									py: 1.5,
									transition: "background-color 0.15s ease",
									"&:hover": {
										bgcolor: alpha(theme.palette.primary.main, 0.04),
									},
									position: "relative",
									"&::before": {
										content: '""',
										position: "absolute",
										left: 0,
										top: 0,
										bottom: 0,
										width: 3,
										bgcolor: accentColor,
										opacity: 0.6,
									},
								}}
							>
								<Avatar
									sx={{
										width: 40,
										height: 40,
										fontWeight: 700,
										bgcolor: alpha(theme.palette.primary.main, 0.12),
										color: "primary.main",
									}}
								>
									{hasInitials ? (
										initials
									) : (
										<PersonIcon sx={{ fontSize: 20 }} />
									)}
								</Avatar>

								<Box sx={{ flex: 1, minWidth: 0 }}>
									<SATOSLink to="UserCompetencyPage" params={[student.Id]}>
										<Tooltip title={student.Name ?? ""} enterDelay={400}>
											<Typography
												variant="body2"
												fontWeight={600}
												noWrap
												sx={{
													color: "text.primary",
													"&:hover": { textDecoration: "underline" },
												}}
											>
												{student.Name}
											</Typography>
										</Tooltip>
									</SATOSLink>
								</Box>

								{areEditable ? (
									<Tooltip
										title={
											<Translate String="CourseSessionStudents.Remove">
												Remove
											</Translate>
										}
										enterDelay={400}
									>
										<IconButton
											aria-label="remove participant"
											size="small"
											onClick={() => {
												void handleDelete(sessionId, student.Id);
											}}
											sx={{
												color: "error.main",
												"&:hover": {
													bgcolor: alpha(theme.palette.error.main, 0.1),
												},
											}}
										>
											<DeleteForeverIcon sx={{ fontSize: 18 }} />
										</IconButton>
									</Tooltip>
								) : (
									<Chip
										size="small"
										variant="outlined"
										color={markToColor(student.Mark)}
										label={student.Mark ?? "—"}
										sx={{
											fontWeight: 600,
											height: 26,
										}}
									/>
								)}
							</Box>
						</ListItem>
					);
				})}
			</List>
		</Paper>
	);
};
